import React from 'react';

import GlobalStyles from '@mui/material/GlobalStyles';
import { GRID_SERVICE_KEYS } from '@/components/AgGrid/helpers';

export const ROW_HEIGHT = 56;
// 8px visible row gap inside the ROW_HEIGHT slot.
const INNER_MAX_HEIGHT = 48;

const AgGridStyles: React.FC = () => (
  <GlobalStyles
    styles={(theme) => ({
      '.ag-theme-dokka.ag-theme-dokka--bank-feed-transactions': {
        // Pull ONLY the page-size selector to the left edge; the row-count summary and page-nav
        // stay right (AG default flex-end). `margin-right: auto` in the flex row pushes everything
        // after the selector to the far right. Bank-feed-scoped per DA-19459 feedback.
        '& .ag-paging-panel .ag-paging-page-size': {
          marginRight: 'auto',
        },

        '& .ag-root-wrapper': {
          overflow: 'visible',
        },

        '& .ag-paging-panel': {
          backgroundColor: 'var(--ag-background-color)',
          marginInline: `calc(-1 * ${theme.spacing(2)})`,
          paddingInline: theme.spacing(2),
          [theme.breakpoints.up('sm')]: {
            marginInline: `calc(-1 * ${theme.spacing(3)})`,
            paddingInline: theme.spacing(3),
          },
        },

        '& .ag-status-bar': {
          marginInline: `calc(-1 * ${theme.spacing(2)})`,
          paddingInline: `calc(var(--ag-spacing) * 4 + ${theme.spacing(2)})`,
          borderTop: 'none',
          [theme.breakpoints.up('sm')]: {
            marginInline: `calc(-1 * ${theme.spacing(3)})`,
            paddingInline: `calc(var(--ag-spacing) * 4 + ${theme.spacing(3)})`,
          },
        },

        '& .ag-status-bar:has(.ag-status-name-value[data-visible])': {
          borderTop: 'solid var(--ag-border-width) var(--ag-border-color)',
        },

        '& .ag-header-cell-label': {
          justifyContent: 'flex-start',
        },

        // Columns-visibility service column header: padding + a border delimiter, mirroring the
        // Documents/Workspace grid. The pinned-right header wrapper rule below clears the pinned
        // edge on the right-pinned header; this `!important` rule keeps the service column's own
        // border (order-independent). No borderRadius: a physical radius would round
        // the wrong edge in RTL (the column pins left there). Also re-center its icon button — the
        // global `.ag-header-cell-label { flex-start }` left-align is for the text data columns.
        [`& .ag-header-cell.${GRID_SERVICE_KEYS.COLUMNS_VISIBILITY_MENU}`]: {
          padding: theme.spacing(0, 0.5),
          border: '1px solid var(--ag-border-color) !important',
          '& .ag-header-cell-label': {
            justifyContent: 'center',
          },
        },

        '& .ag-row': {
          maxHeight: INNER_MAX_HEIGHT,
          cursor: 'pointer',
        },

        '& .ag-cell': {
          borderTop: '1px solid var(--ag-border-color)',
          borderBottom: '1px solid var(--ag-border-color)',
        },

        '& .ag-column-first': {
          borderLeft: '1px solid var(--ag-border-color)',
          borderRadius: '8px 0 0 8px',
        },

        '& .ag-column-last': {
          borderRight: '1px solid var(--ag-border-color) !important',
          borderRadius: '0 8px 8px 0',
        },

        // v36 draws the pinned header edge on the container's inner wrapper
        '& .ag-header-row .ag-grid-pinned-right-cells > .ag-grid-container-wrapper': {
          border: 'none !important',
          boxShadow: 'none !important',
        },

        '& .ag-grid-scrolling-rows .ag-grid-pinned-left-cells .ag-cell-last-left-pinned, & .ag-grid-scrolling-rows .ag-grid-pinned-right-cells .ag-cell-first-right-pinned':
          {
            boxShadow: 'none',
          },

        // Cursor pagination can't jump to an arbitrary "last" page (we only know cursors for
        // sequentially-visited pages), so hide AG Grid's Last-page button. First-page stays
        // visible (cursor is always null on page 0 — safe to jump to start any time).
        // RTL flips visual position → in RTL the "last" icon class is rendered for what users
        // see as the first button and vice versa.
        [`& .ag-icon.ag-icon-${theme.direction === 'rtl' ? 'first' : 'last'}`]: {
          display: 'none',
        },
      },
    })}
  />
);

export default AgGridStyles;
